import { lazy } from 'react';

// Chunk-load failures happen when a browser keeps an old index.html after a
// deploy: the previous build's chunk hashes no longer exist, so the dynamic
// import 404s ("Loading chunk N failed" / ChunkLoadError). Retrying the same
// import cannot succeed — the file is gone. One hard reload fetches the new
// index.html and its chunk map. A session key guards against reload loops.

const RELOAD_KEY = 'synoti:chunk-reload-at';
const RELOAD_COOLDOWN_MS = 10000;

const CHUNK_ERROR_RE =
  /Loading chunk \d+ failed|Loading CSS chunk|ChunkLoadError|Loading module chunk|Importing a module script failed|Failed to fetch dynamically imported module/i;

export function isChunkLoadError(error) {
  if (!error) return false;
  const msg = String(error.message || error);
  return CHUNK_ERROR_RE.test(msg);
}

export function reloadForNewBuild() {
  const now = Date.now();
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch (e) {
    /* private mode — treat as first failure */
  }
  if (now - last < RELOAD_COOLDOWN_MS) return false;
  try {
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch (e) {
    /* ignore */
  }
  window.location.reload();
  return true;
}

export function loadWithRetry(factory) {
  return Promise.resolve()
    .then(factory)
    .catch((error) => {
      if (isChunkLoadError(error) && reloadForNewBuild()) {
        return new Promise(() => {}); // keep Suspense pending until reload
      }
      throw error;
    });
}

export function lazyRetry(factory) {
  return lazy(() => loadWithRetry(factory));
}

// Webpack's own chunk loading (CSS chunks, non-lazy boundaries) surfaces as a
// window error / unhandled rejection instead of a React.lazy rejection.
export function installChunkErrorGuard() {
  const onError = (event) => {
    const error = event?.error || event?.reason;
    if (isChunkLoadError(error || event?.message)) reloadForNewBuild();
  };
  window.addEventListener('error', onError, true);
  window.addEventListener('unhandledrejection', onError);
  return () => {
    window.removeEventListener('error', onError, true);
    window.removeEventListener('unhandledrejection', onError);
  };
}
